import { IconChevronDown } from '@tabler/icons-react';
import type { ChangeEvent } from 'react';

import { cn } from '../../../lib/cn';

/**
 * The hours-and-minutes half of the two date-time pickers.
 *
 * Two real `<select>` elements, `appearance-none` and restyled from tokens, so
 * the closed control carries the house treatment while the open list keeps the
 * platform's own behaviour — which is what makes it usable with a keyboard, a
 * screen reader and a touch screen without a single line of our own popup code.
 *
 * The value is 24-hour `HH:mm`. 12-hour display is the locale's business and
 * belongs in the formatter, not in the control: the picker feeds this straight
 * into `toIsoDateTime`, and an AM/PM column here would have to be un-picked
 * before that call — one more place to get midnight wrong.
 */
export interface TimeFieldProps {
  /** Base id — the two selects become `${id}-hours` and `${id}-minutes`. */
  id: string;
  /** `HH:mm`, 24-hour. */
  value: string;
  onChange: (value: string) => void;
  /** Minutes offered between 0 and 59. Default 5. */
  minuteStep?: number;
  disabled?: boolean;
  /** The visible group label. Omit it when the surrounding picker already says it. */
  label?: string;
}

const SELECT_CLASSES = cn(
  'dhd-target relative h-10 w-full appearance-none rounded-xs border-2 bg-surface pl-2.5 pr-7',
  'font-display text-sm text-heading',
  'transition-[border-color,background-color] duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
  'disabled:pointer-events-none disabled:opacity-45',
  'border-line-strong hover:border-heading',
);

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

export function TimeField({
  id,
  value,
  onChange,
  minuteStep = 5,
  disabled = false,
  label,
}: TimeFieldProps) {
  const [rawHours = '', rawMinutes = ''] = value.split(':');
  // A value that is not a two-part clock string must not make either select
  // uncontrolled, which is what an `undefined` value would do.
  const hours = rawHours.padStart(2, '0');
  const minutes = rawMinutes.padStart(2, '0');

  const hourOptions: string[] = [];
  for (let hour = 0; hour < 24; hour += 1) hourOptions.push(pad(hour));

  const minuteOptions: string[] = [];
  for (let minute = 0; minute < 60; minute += minuteStep) minuteOptions.push(pad(minute));

  const emit = (nextHours: string, nextMinutes: string) => {
    onChange(`${nextHours}:${nextMinutes}`);
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label ? (
        <span className="font-display text-2xs font-semibold uppercase tracking-label text-heading">
          {label}
        </span>
      ) : null}

      <div className="flex items-center gap-1.5">
        <div className="relative w-[4.5rem]">
          <select
            id={`${id}-hours`}
            aria-label={label ? `${label} — hours` : 'Hours'}
            value={hours}
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => emit(event.target.value, minutes)}
            className={SELECT_CLASSES}
          >
            {hourOptions.map((hour) => (
              <option key={hour} value={hour}>
                {hour}
              </option>
            ))}
          </select>
          <IconChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-muted"
          />
        </div>

        <span aria-hidden="true" className="font-display text-sm text-muted">
          :
        </span>

        <div className="relative w-[4.5rem]">
          <select
            id={`${id}-minutes`}
            aria-label={label ? `${label} — minutes` : 'Minutes'}
            value={minutes}
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => emit(hours, event.target.value)}
            className={SELECT_CLASSES}
          >
            {minuteOptions.map((minute) => (
              <option key={minute} value={minute}>
                {minute}
              </option>
            ))}
          </select>
          <IconChevronDown
            aria-hidden="true"
            className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-muted"
          />
        </div>
      </div>
    </div>
  );
}
